import type { ReactNode } from 'react';
import clsx from 'clsx';

import { useStyles } from './styles';

export interface PillTabItem<V extends string> {
  value: V;
  label: ReactNode;
  disabled?: boolean;
}

interface PillTabsProps<V extends string> {
  items: PillTabItem<V>[];
  value: V;
  onChange: (value: V) => void;
  /** soft: light-blue selection · solid: filled selection · group: bordered toggle */
  variant?: 'soft' | 'solid' | 'group';
  /**
   * What the control is to assistive tech: switching panels (tabs), choosing one
   * value for a form (radio), or a filter toggle (toggle).
   */
  semantics?: 'tabs' | 'radio' | 'toggle';
  ariaLabel?: string;
  className?: string;
}

const ROLE = { tabs: 'tablist', radio: 'radiogroup', toggle: 'group' } as const;

export const PillTabs = <V extends string>({
  items,
  value,
  onChange,
  variant = 'soft',
  semantics = 'tabs',
  ariaLabel,
  className
}: PillTabsProps<V>) => {
  const classes = useStyles();
  const onClass = {
    soft: classes.softOn,
    solid: classes.solidOn,
    group: classes.groupOn
  }[variant];

  const stateProps = (selected: boolean) => {
    if (semantics === 'tabs') return { role: 'tab', 'aria-selected': selected };
    if (semantics === 'radio')
      return { role: 'radio', 'aria-checked': selected };
    return { 'aria-pressed': selected };
  };

  return (
    <div
      role={ROLE[semantics]}
      aria-label={ariaLabel}
      className={clsx(
        classes.list,
        variant === 'group' && classes.groupList,
        className
      )}>
      {items.map(item => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            disabled={item.disabled}
            className={clsx(
              classes.item,
              classes[variant],
              selected && onClass
            )}
            onClick={() => onChange(item.value)}
            {...stateProps(selected)}>
            {item.label}
          </button>
        );
      })}
    </div>
  );
};
